import { forwardRef, ReactNode } from 'react';
import {
    RiAlertLine,
    RiCheckboxCircleLine,
    RiErrorWarningLine,
    RiInformationLine,
} from '@remixicon/react';
import { Alert } from '../Alert/Alert';
import { DesignSystemAlertColorScheme } from '../../theme/design-system/types';
import { VariantType } from '../../vendor/notistack';
import { IconProps } from '../Icon/RemixIcon';

export type SnackbarContentProps = {
    variant?: VariantType;
    message?: ReactNode;
    icon?: ReactNode;
    description?: ReactNode;
    layout?: 'inline' | 'stacked';
    onClose?: VoidFunction;
};

const variantToColorScheme: Record<
    VariantType | 'default',
    DesignSystemAlertColorScheme
> = {
    success: 'positive',
    error: 'critical',
    warning: 'attention',
    info: 'neutral',
    default: 'neutral',
};

const variantToIcon: Record<VariantType | 'default', IconProps['icon']> = {
    success: RiCheckboxCircleLine,
    error: RiAlertLine,
    warning: RiErrorWarningLine,
    info: RiInformationLine,
    default: RiInformationLine,
};

export const SnackbarContent = forwardRef<HTMLDivElement, SnackbarContentProps>(
    function SnackbarContent(props, ref) {
        const {
            variant = 'default',
            message,
            icon,
            description,
            layout,
            onClose,
        } = props;

        const colorScheme = variantToColorScheme[variant] ?? 'neutral';
        const defaultIcon = variantToIcon[variant] ?? RiInformationLine;
        const stacking = layout ?? (description ? 'stacked' : 'inline');

        return (
            <div ref={ref}>
                <Alert
                    colorScheme={colorScheme}
                    size="md"
                    stacking={stacking}
                    onClose={onClose}
                >
                    {icon ? (
                        <Alert.Icon icon={() => <>{icon}</>} />
                    ) : (
                        <Alert.Icon icon={defaultIcon} />
                    )}
                    <Alert.Title>{message}</Alert.Title>
                    {description && (
                        <Alert.Content>{description}</Alert.Content>
                    )}
                </Alert>
            </div>
        );
    },
);
